import { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Loader2, Check, Megaphone } from "lucide-react";
import api, { formatErr } from "@/lib/api";

const MAX_SECONDS = 15;

export default function VoiceDropBox() {
  const [isRecording, setIsRecording] = useState(false);
  const [remaining, setRemaining] = useState(MAX_SECONDS);
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const pickMime = () => {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    for (const m of candidates) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  };

  const startRecording = async () => {
    setError(""); setSuccess(false);
    chunksRef.current = [];

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Microphone not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        await uploadDrop(blob, type);
      };

      rec.start();
      setIsRecording(true);
      setRemaining(MAX_SECONDS);

      timerRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error("mic error", err);
      setError("Couldn't access your microphone. Check browser permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    setIsRecording(false);
  };

  const uploadDrop = async (blob, mime) => {
    setIsUploading(true);
    try {
      const ext = mime.includes("webm") ? "webm" : mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("audio_drop", blob, `drop_${Date.now()}.${ext}`);
      if (label.trim()) fd.append("listener_label", label.trim());
      await api.post("/voice-drops/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      setLabel("");
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || "Couldn't send your drop. Try again!";
      setError(msg);
      setTimeout(() => setError(""), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const progress = ((MAX_SECONDS - remaining) / MAX_SECONDS) * 100;

  return (
    <section className="w-full max-w-[420px] mx-auto mt-10" data-testid="voice-drop-section">
      <div className="player-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone size={16} className="text-[var(--brand-purple-light)]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--brand-purple-light)]">
            Listener Studio Line
          </span>
        </div>
        <h3 className="font-display text-xl font-bold mb-1">Send a Voice Drop</h3>
        <p className="text-[var(--text-muted)] text-sm mb-5">
          Shout-outs, requests, or call-ins straight to <span className="text-white">JAVI G</span>'s mixer.
        </p>

        {/* Optional listener label (e.g. name or city) */}
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={60}
          disabled={isRecording || isUploading}
          placeholder="Your name or city (optional)"
          className="input-dark mb-3"
          data-testid="voice-drop-label-input"
        />

        {/* Action button */}
        {!isRecording ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={isUploading}
            data-testid="voice-drop-start-button"
            className={`w-full py-3.5 rounded-md font-mono text-sm font-semibold flex items-center justify-center gap-2 transition-all border ${
              success
                ? "bg-[rgba(0,242,254,0.1)] border-[var(--neon-cyan)] text-[var(--neon-cyan)]"
                : isUploading
                ? "bg-[#1e1e2d] border-[#2a2a3d] text-[var(--text-muted)] cursor-wait"
                : "bg-gradient-to-r from-[#7a1fa2] to-[#a855f7] border-[var(--brand-purple-light)] text-white hover:shadow-[0_8px_25px_rgba(168,85,247,0.4)] hover:-translate-y-0.5"
            }`}
          >
            {isUploading ? (
              <><Loader2 size={16} className="animate-spin" /> Sending to studio…</>
            ) : success ? (
              <><Check size={16} /> Voice Drop sent!</>
            ) : (
              <><Mic size={16} /> Tap to record (15s max)</>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="voice-drop-stop-button"
            className="w-full py-3.5 rounded-md font-mono text-sm font-semibold relative overflow-hidden border border-[#ff5566] bg-[rgba(255,85,102,0.1)] text-white"
          >
            {/* progress fill */}
            <span
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#ff3344]/40 to-[#ff5566]/40 transition-[width] duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
            <span className="relative z-10 flex items-center justify-center gap-2">
              <span className="live-dot"></span>
              <MicOff size={16} /> Recording… tap to stop · <span className="font-mono">{remaining}s</span>
            </span>
          </button>
        )}

        {error && (
          <p className="text-[#ff5566] text-xs font-mono mt-3 text-center" data-testid="voice-drop-error">
            {error}
          </p>
        )}

        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] text-center mt-4">
          Anonymous · {MAX_SECONDS}s max · be kind, keep it musical
        </p>
      </div>
    </section>
  );
}
